import { useState } from 'react';
import { Check, Link } from 'lucide-react';
import { toast } from 'sonner';
import { Button, Dialog, DialogContent, DialogHeader, DialogTitle } from '@artstorefronts/ui';
import { useTranslation } from 'react-i18next';
import { useAnalytics } from '../context/analytics';
import {
  FacebookShareButton,
  PinterestShareButton,
  RedditShareButton,
  TwitterShareButton,
  WhatsappShareButton,
  LinkedinShareButton,
  BlueskyShareButton,
  ThreadsShareButton,
  FacebookIcon,
  PinterestIcon,
  RedditIcon,
  WhatsappIcon,
  LinkedinIcon,
  BlueskyIcon,
  ThreadsIcon,
  XIcon,
} from '@artstorefronts/react-share';
import { Icon } from './Icon';

interface ShareModalProps {
  isOpen: boolean;
  onClose: () => void;
  url: string;
  title?: string;
  quote?: string;
  showPostToInstagram?: boolean;
  onPostToInstagram?: () => void;
}

export default function ShareModal({
  isOpen,
  onClose,
  url,
  title,
  quote,
  showPostToInstagram = false,
  onPostToInstagram,
}: ShareModalProps) {
  const [copied, setCopied] = useState(false);
  const { t } = useTranslation('social');
  const { t: tc } = useTranslation('common');
  const { amplitude, intercom } = useAnalytics();

  const handleShareClick = (
    platform: 'facebook' | 'pinterest' | 'reddit' | 'twitter' | 'whatsapp' | 'linkedin' | 'bluesky' | 'threads' | 'copy'
  ) => {
    amplitude.track('quick_share_button_clicked', {
      platform,
      url,
    });
    intercom.trackEvent('quick_share_button_clicked', {
      platform,
      url,
    });
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success(tc('copied-to-clipboard'));
      handleShareClick('copy');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy: ', err);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-[calc(100%-2rem)] sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="mb-0">
            {showPostToInstagram ? t('share-modal.post-to-social') : t('share-modal.share')}
          </DialogTitle>
        </DialogHeader>

        {/* Social Share Buttons */}
        <div className="px-3 sm:px-6 pb-4">
          <span className="text-sm font-bold mb-2">{t('share-modal.quick-share')}</span>
          <p className="text-xs text-gray-600 mb-4">{t('share-modal.quick-share-hint')}</p>
          <div className="flex flex-row justify-center sm:justify-between flex-wrap gap-3">
            <FacebookShareButton url={url} onClick={() => handleShareClick('facebook')}>
              <FacebookIcon size={32} round />
            </FacebookShareButton>

            <ThreadsShareButton url={url} title={title} onClick={() => handleShareClick('threads')}>
              <ThreadsIcon size={32} round />
            </ThreadsShareButton>

            <WhatsappShareButton url={url} title={title} separator=":: " onClick={() => handleShareClick('whatsapp')}>
              <WhatsappIcon size={32} round />
            </WhatsappShareButton>

            <TwitterShareButton url={url} title={title} onClick={() => handleShareClick('twitter')}>
              <XIcon size={32} round />
            </TwitterShareButton>

            <PinterestShareButton
              url={url}
              media={url}
              description={quote}
              onClick={() => handleShareClick('pinterest')}
            >
              <PinterestIcon size={32} round />
            </PinterestShareButton>

            <LinkedinShareButton url={url} title={title} summary={quote} onClick={() => handleShareClick('linkedin')}>
              <LinkedinIcon size={32} round />
            </LinkedinShareButton>

            <BlueskyShareButton url={url} title={title} onClick={() => handleShareClick('bluesky')}>
              <BlueskyIcon size={32} round />
            </BlueskyShareButton>

            <RedditShareButton url={url} title={title} onClick={() => handleShareClick('reddit')}>
              <RedditIcon size={32} round />
            </RedditShareButton>

            <button
              onClick={handleCopy}
              className="flex items-center justify-center w-8 h-8 rounded-full bg-gray-200 hover:bg-gray-300 transition-colors cursor-pointer"
              title={tc('copy-to-clipboard')}
            >
              {copied ? <Check className="w-4 h-4 text-green-600" /> : <Link className="w-4 h-4 text-black" />}
            </button>
          </div>
        </div>

        {showPostToInstagram && onPostToInstagram && (
          <>
            <div className="flex items-center mx-3 sm:mx-6">
              <div className="flex-1 border-t border-gray-300"></div>
              <span className="px-3 text-sm text-gray-500 bg-white">{tc('or')}</span>
              <div className="flex-1 border-t border-gray-300"></div>
            </div>
            <div className="px-3 sm:px-6 flex gap-4 items-center mb-3">
              <div>
                <span className="text-sm font-bold mb-2">{t('share-modal.build-from-scratch')}</span>
                <p className="text-xs text-gray-600">{t('share-modal.build-from-scratch-hint')}</p>
              </div>

              <Button className="flex items-center pr-2" onClick={onPostToInstagram}>
                {tc('go')}
                <Icon icon="chevron-right" className="h-4 w-4" />
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
